/**
 * 聊天文本的纯逻辑：表情插入、@提醒的解析与匹配。
 *
 * 与界面分开写，是为了把「@谁」这件事的规则钉死在单测里：
 * 中文名字后面通常不打空格（「@王建国老师你好」），
 * 不能靠空格切词，只能拿在场成员的名字做最长匹配。
 */

/** 聊天区表情面板的候选。纯文本字符，不经服务端白名单。 */
export const CHAT_EMOJIS = [
  '😀', '😂', '😊', '😉', '😍', '🤔', '😮', '😢',
  '😡', '👍', '👎', '👏', '🙏', '💪', '🤝', '❤️',
  '🎉', '✅', '❌', '❓', '💡', '📖', '✏️', '⏰',
]

/** 全体提醒用的名字。 */
export const MENTION_ALL = '所有人'

export interface Segment {
  type: 'text' | 'mention'
  value: string
}

/** 在光标处插入片段，返回新文本和新光标位置。 */
export function insertAt(text: string, caret: number, snippet: string): { text: string; caret: number } {
  const pos = Math.max(0, Math.min(caret, text.length))
  return { text: text.slice(0, pos) + snippet + text.slice(pos), caret: pos + snippet.length }
}

/**
 * 光标前是不是正在输入一个 @。
 * 返回 @ 的位置和 @ 后已输入的查询串；中途遇到空白或换行就算结束。
 */
export function mentionQuery(text: string, caret: number): { start: number; query: string } | null {
  const head = text.slice(0, caret)
  const at = head.lastIndexOf('@')
  if (at < 0) return null
  const query = head.slice(at + 1)
  if (/\s/.test(query)) return null
  // @ 紧跟在字母数字后面多半是邮箱，不是提醒。
  if (at > 0 && /[A-Za-z0-9]/.test(head[at - 1])) return null
  return { start: at, query }
}

/** 选中候选后把 `@查询串` 换成 `@名字 `。 */
export function applyMention(text: string, caret: number, start: number, name: string) {
  const snippet = `@${name} `
  return { text: text.slice(0, start) + snippet + text.slice(caret), caret: start + snippet.length }
}

/** 按查询串过滤候选名字：前缀优先，其次包含；空查询返回全部。 */
export function filterNames(names: string[], query: string): string[] {
  const q = query.toLowerCase()
  if (!q) return names
  const starts = names.filter((n) => n.toLowerCase().startsWith(q))
  const has = names.filter((n) => !n.toLowerCase().startsWith(q) && n.toLowerCase().includes(q))
  return [...starts, ...has]
}

/**
 * 把一条消息拆成普通文本和 @提醒两种片段。
 * 只认在场成员的名字（加上「所有人」），同一位置取最长的那个名字，
 * 否则「@王建」会把「@王建国老师」切坏。
 */
export function tokenize(text: string, names: Iterable<string>): Segment[] {
  const sorted = [...new Set([...names, MENTION_ALL])].filter(Boolean).sort((a, b) => b.length - a.length)
  const out: Segment[] = []
  let buf = ''
  let i = 0
  while (i < text.length) {
    if (text[i] === '@') {
      const hit = sorted.find((n) => text.startsWith(n, i + 1))
      if (hit) {
        if (buf) out.push({ type: 'text', value: buf })
        buf = ''
        out.push({ type: 'mention', value: hit })
        i += 1 + hit.length
        continue
      }
    }
    buf += text[i]
    i++
  }
  if (buf) out.push({ type: 'text', value: buf })
  return out
}

/** 这条消息是否点到了我（直接 @我，或 @所有人）。 */
export function mentionsMe(text: string, myName: string, names: Iterable<string>): boolean {
  if (!text.includes('@')) return false
  return tokenize(text, names).some(
    (s) => s.type === 'mention' && (s.value === MENTION_ALL || (!!myName && s.value === myName)),
  )
}
